import { Fragment, memo, useId, useMemo, useRef, useState } from "react";
import {
  Combobox,
  Mark,
  Portal,
  Stack,
  TagsInput,
  Text,
  createListCollection,
  useCombobox,
  useFilter,
  useTagsInput,
} from "@chakra-ui/react";
import { highlightChunks } from "@/lib/highlightMatch";

/** How many options are put in the DOM at once. A search box, not a list. */
const MAX_VISIBLE = 50;

export type MultiSelectOption = {
  /** Stored value — an id, not the label. */
  value: string;
  label: string;
  /** Shown under the label and searched alongside it: an address, a key. */
  hint?: string;
};

function HighlightedText({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightChunks(text, query).map((chunk, index) =>
        chunk.match
          ? <Mark key={index} variant="subtle" colorPalette="blue">{chunk.text}</Mark>
          : <Fragment key={index}>{chunk.text}</Fragment>)}
    </>
  );
}

/**
 * Pick several things from a long list.
 *
 * Built the way Chakra documents a combobox with tags: two machines created by
 * hand with **the same element ids**, so they drive one input and one control
 * rather than each looking for its own. That shared id is what lets Backspace
 * and the arrow keys reach the tags — the tags-input handler defers to the
 * combobox while the listbox is open and takes over when it is closed.
 *
 * Values are ids and labels are text: `ItemText` renders the children it is
 * given, so the two need not be the same string. Typed text can never become a
 * value — `validate` accepts only ids that are in `options` — so a selection
 * is always something that exists.
 */
export const MultiSelectTags = memo(function MultiSelectTags({
  label,
  placeholder,
  options,
  selected,
  onChange,
  tagLabelFor,
  emptyMessage = "Nothing matches that.",
  helperText,
  hideLabel = false,
}: {
  label: string;
  placeholder?: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Override the tag text, where the plain label would be ambiguous. */
  tagLabelFor?: (value: string) => string;
  emptyMessage?: string;
  helperText?: string;
  /** Kept for screen readers, hidden where a section heading already says it. */
  hideLabel?: boolean;
}) {
  const { contains } = useFilter({ sensitivity: "base" });
  // `useFilter` hands back a NEW `contains` on every render, so naming it
  // as a dependency below would rebuild the list on every render of the
  // whole page -- which, in the activity editor, is every keystroke in any
  // field. Held in a ref so the memo depends on what actually changed.
  const containsRef = useRef(contains);
  containsRef.current = contains;

  const [query, setQuery] = useState("");
  // Rendering the list costs whether or not anybody is looking at it: a
  // closed combobox still mounts every option. With the institute in the
  // list that was three hundred nodes re-rendered on each keystroke.
  const [open, setOpen] = useState(false);
  const uid = useId();

  // Rebuilt from `options` rather than seeded once: the list usually arrives
  // asynchronously, and a collection created on the first render would stay
  // empty for ever.
  const collection = useMemo(() => {
    const trimmed = query.trim();
    const match = containsRef.current;
    const matching = trimmed === ""
      ? options
      : options.filter((option) =>
        match(option.label, trimmed) || match(option.hint ?? "", trimmed));
    return createListCollection({
      items: matching,
      itemToString: (option) => option.label,
      itemToValue: (option) => option.value,
    });
  }, [options, query]);

  // Only ever a screenful. Somebody scrolling three hundred names is not
  // finding anybody; the search box is the way through a list this long,
  // and the count below says so rather than leaving the list looking
  // complete when it is not.
  const shown = collection.items.slice(0, MAX_VISIBLE);
  const hidden = collection.items.length - shown.length;

  const labelOf = (value: string) =>
    tagLabelFor?.(value) ?? options.find((option) => option.value === value)?.label ?? "Unknown";

  const ids = { input: `multiselect-input-${uid}`, control: `multiselect-control-${uid}` };

  const tags = useTagsInput({
    ids,
    value: selected,
    onValueChange: (details) => onChange(details.value),
    editable: false,
    // The only gate on what may become a tag: ids from the combobox pass,
    // anything typed does not.
    validate: ({ inputValue }) => options.some((option) => option.value === inputValue),
  });

  const combobox = useCombobox({
    ids,
    collection,
    // The combobox holds no selection of its own; choosing hands the value to
    // the tags and clears, so the list of choices exists once.
    value: [],
    selectionBehavior: "clear",
    openOnClick: true,
    onOpenChange: (details) => setOpen(details.open),
    onValueChange: (details) => {
      const value = details.value[0];
      if (value !== undefined) {
        tags.addValue(value);
      }
    },
    onInputValueChange: (details) => setQuery(details.inputValue),
  });

  return (
    <Combobox.RootProvider value={combobox}>
      <TagsInput.RootProvider value={tags}>
        <TagsInput.Label fontSize="sm" srOnly={hideLabel}>{label}</TagsInput.Label>

        <TagsInput.Control>
          {selected.map((value, index) => (
            <TagsInput.Item key={value} index={index} value={value}>
              <TagsInput.ItemPreview>
                <TagsInput.ItemText>{labelOf(value)}</TagsInput.ItemText>
                <TagsInput.ItemDeleteTrigger aria-label={`Remove ${labelOf(value)}`} />
              </TagsInput.ItemPreview>
            </TagsInput.Item>
          ))}

          <Combobox.Input unstyled asChild>
            <TagsInput.Input placeholder={selected.length === 0 ? placeholder : ""} />
          </Combobox.Input>
        </TagsInput.Control>

        {helperText ? (
          <Text mt={1} fontSize="xs" color="fg.muted">{helperText}</Text>
        ) : null}

        <Portal>
          <Combobox.Positioner>
            <Combobox.Content maxH="64" overflowY="auto">
              <Combobox.Empty>{emptyMessage}</Combobox.Empty>
              {(open ? shown : []).map((option: MultiSelectOption) => (
                <Combobox.Item key={option.value} item={option}>
                  <Stack gap={0}>
                    <Combobox.ItemText>
                      <HighlightedText text={option.label} query={query} />
                    </Combobox.ItemText>
                    {option.hint ? (
                      <Text fontSize="xs" color="fg.muted">
                        <HighlightedText text={option.hint} query={query} />
                      </Text>
                    ) : null}
                  </Stack>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
              {open && hidden > 0 ? (
                <Text px={3} py={2} fontSize="xs" color="fg.muted">
                  {hidden} more — keep typing to narrow it down.
                </Text>
              ) : null}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </TagsInput.RootProvider>
    </Combobox.RootProvider>
  );
});
